import {
  Button,
  FormControlLabel,
  TextField,
  FormControl,
  FormGroup,
  Box,
  Stack,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  ArrowForward as ArrowForwardIcon,
  SubscriptionsOutlined as SubscriptionsIcon,
} from '@mui/icons-material'
import { Outlet } from 'react-router'
import { useCallback, useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'es-toolkit/compat'
import {
  getTranslatedName,
  getYearsWithHarvests,
  isSubscriptionValid,
} from 'common/misc'
import {
  getHarvestsByYear,
  getHarvestsByFarm,
  getHarvestsByClient,
} from 'api/harvest'
import {
  startFarmReport,
  startFieldReport,
  startHarvesterReport,
} from 'api/report'
import HarvestReport from './harvest-report'
import { CommonSidebar } from 'components/common-sidebar'
import SwathParameterSelector, {
  swathParameters,
} from 'components/swath-selector'
import { fetchDeletedFields, getLayerInfoByHarvest } from 'api/field'
import colors from 'common/colors'
import { trackEvent } from 'api/usage-tracking'
import { TEST_IDS } from '../../test-ids'

export default function Reports(props) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const defaultLayerOptions = [
    {
      key: 'corrected_yield',
      name: `${t('corrected_yield_map')}`,
    },
    {
      key: 'zones_yield',
      name: `${t('classified_yield_map')}`,
    },
    {
      key: 'trend_yield',
      name: `${t('yield_map_trend')}`,
    },
    {
      key: 'swath',
      name: `${t('swath_map')}`,
    },
  ].sort((a, b) => {
    if (a.name > b.name) return 1
    else if (a.name < b.name) return -1
    return 0
  })

  const harvestYears = useSelector((state) => state.harvest.years)
  const harvestsWithYears = useSelector((state) => state.harvest.byYear)
  const fields = useSelector((state) => state.field['collection'])
  const crops = useSelector((state) => state.crop['cropConstantsCollection'])
  const fetchedClients = useSelector((state) => state.client.collection)
  const fetchedFarms = useSelector((state) => state.farm.collection)
  const token = useSelector((state) => state.account['token'])
  const layerInfo = useSelector((state) => state.field['layerInfo'])
  const subscriptions = useSelector((state) => state.account.subscriptions)

  const cffPreferences = useSelector(
    (state) => state.account.singleton.prefs?.cff,
  )
  const userPrefs = useSelector((state) => state.account.singleton.prefs)

  const [farms, setFarms] = useState([{ id: -1, name: t('all') }])
  const [allFields, setAllFields] = useState(null)
  const [harvestsWithMaps, setHarvestsWithMaps] = useState([])
  const [selectedHarvest, setSelectedHarvest] = useState({})
  const [layerOptions, setLayerOptions] = useState(defaultLayerOptions)
  const [selectedLayer, setSelectedLayerType] = useState(defaultLayerOptions[0])
  const [harvestToReport, setHarvestToReport] = useState({})
  const [layerToReport, setLayerToReport] = useState({})
  const [selectedClient, setSelectedClient] = useState(undefined)
  const [selectedFarm, setSelectedFarm] = useState(undefined)
  const [generateClicked, setGenerateClicked] = useState(false)
  const [layerInfoMap, setLayerInfoMap] = useState({})
  const [activeStatus, setActiveStatus] = useState(false)
  const [swathParameter, setSwathParameter] = useState(swathParameters[0])
  const [swathSldExtension, setSwathSldExtension] = useState(undefined)
  const [swathSldExtensionToReport, setSwathSldExtensionToReport] =
    useState(undefined)

  const [reportType, setReportType] = useState('field_harvest')
  const [format, setFormat] = useState('xlsx')
  const [includeDryYield, setIncludeDryYield] = useState(false)
  const [startDate, setStartDate] = useState(undefined)
  const [areaUnit, setAreaUnit] = useState('ha')
  const [yieldUnit, setYieldUnit] = useState('tonnes_ha')
  const [totalYieldUnit, setTotalYieldUnit] = useState('kg')
  const [speedUnit, setSpeedUnit] = useState('kmh')

  const yearsWithHarvests = useMemo(
    () => getYearsWithHarvests(harvestYears),
    [harvestYears],
  )

  const [selectedYear, setSelectedYear] = useState(
    yearsWithHarvests[0] ?? props.defaultYear,
  )

  const clients = [{ id: -1, name: t('all') }, ...fetchedClients]

  useEffect(() => {
    if (yearsWithHarvests.length > 0) {
      onYearChange({ target: { value: yearsWithHarvests[0] } })
    }
  }, [yearsWithHarvests])

  useEffect(() => {
    if (token) {
      if (cffPreferences && cffPreferences.enabled && cffPreferences.client) {
        setSelectedClient(cffPreferences.client)
        dispatch(getHarvestsByClient(cffPreferences.client, selectedYear))
      } else if (
        yearsWithHarvests &&
        yearsWithHarvests.length &&
        !selectedClient &&
        !selectedFarm
      ) {
        setSelectedYear(yearsWithHarvests[0])
        dispatch(getHarvestsByYear(yearsWithHarvests[0]))
      } else {
        let currentYear = new Date().getFullYear()
        setSelectedYear(currentYear)
        if (!selectedClient) {
          dispatch(getHarvestsByYear(currentYear))
        }
      }
    }
  }, [token, yearsWithHarvests, cffPreferences])

  useEffect(() => {
    if (token && fields) {
      fetchDeletedFields().then((deletedFields) => {
        setAllFields([...fields, ...deletedFields])
      })
    }
  }, [token, fields])

  useEffect(() => {
    if (userPrefs?.units) {
      setAreaUnit(userPrefs.units.area_in || 'ha')
      setYieldUnit(userPrefs.units.yieldunit_in || 'tonnes_ha')
      setTotalYieldUnit(userPrefs.units.totalyield_in || 'kg')
      setSpeedUnit(userPrefs.units.speed_in || 'kmh')
    }
  }, [userPrefs?.units])

  useEffect(() => {
    if (fetchedClients && fetchedClients.length) {
      let filteredFarms = null
      if (selectedClient) {
        const foundClient = fetchedClients.find(
          (client) => client.id == selectedClient,
        )
        if (foundClient && foundClient.farms) {
          filteredFarms = foundClient.farms
        }
      } else {
        filteredFarms = fetchedFarms
      }

      if (filteredFarms) {
        if (reportType === 'farm') {
          setFarms(filteredFarms)
          const isSelectedFarmValid = filteredFarms.find(
            (f) => f.id === selectedFarm,
          )
          if (!isSelectedFarmValid && filteredFarms.length > 0) {
            setSelectedFarm(filteredFarms[0].id)
          }
        } else {
          setFarms([{ id: -1, name: t('all') }, ...filteredFarms])
        }
      }
    }
  }, [selectedClient, fetchedClients, fetchedFarms, reportType])

  useEffect(() => {
    if (!isEmpty(layerInfo)) {
      const _layerInfoMap = {}
      const _layerOptions = []
      for (let l = 0; l < defaultLayerOptions.length; l++) {
        if (
          layerInfo.find((li) => li.layerName === defaultLayerOptions[l].key)
        ) {
          _layerOptions.push(defaultLayerOptions[l])
        }
      }
      setLayerOptions(_layerOptions)
      if (_layerOptions.length > 0) {
        setSelectedLayerType(_layerOptions[0])
      } else {
        setSelectedLayerType({})
      }

      layerInfo.map((l) => {
        _layerInfoMap[l.layerName] = l
      })
      setLayerInfoMap(_layerInfoMap)
    }
  }, [layerInfo])

  const handleSwathParameterChange = (event) => {
    setSwathParameter(event.swathParameter)
    setSwathSldExtension(event.sldExtension)
  }

  //Check if the user has active subscription
  useEffect(() => {
    // TODO: Fix the Renew FarmTRX Premium message
    //Use current month and day with selected year as default for checking subscription
    //status. Using only the year can fail if the only valid subscription starts after
    //January 1, which is what just the year is treated as. If there is a valid harvest, use
    //the start and end date of the harvest for the check.
    let defaultHarvest = {}
    const date = new Date()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const period = `${selectedYear}-${month}-${day}`
    let harvestStart = period
    let harvestEnd = period
    if (Object.keys(selectedHarvest).length) {
      defaultHarvest = Object.assign(defaultHarvest, selectedHarvest)
      harvestStart = defaultHarvest.startDate
      harvestEnd = defaultHarvest.endDate
      dispatch(getLayerInfoByHarvest(selectedHarvest.id, selectedYear))
    }
    setActiveStatus(
      isSubscriptionValid(subscriptions, harvestStart, harvestEnd),
    )
  }, [selectedHarvest, selectedYear, subscriptions])

  const onYearChange = useCallback((e) => {
    let year = e.target.value
    setSelectedHarvest({})
    setSelectedYear(year)
    setGenerateClicked(false)

    if (selectedFarm) {
      dispatch(getHarvestsByFarm(selectedFarm, year))
    } else if (selectedClient) {
      dispatch(getHarvestsByClient(selectedClient, year))
    } else {
      dispatch(getHarvestsByYear(year))
    }
  })

  const onClientChange = useCallback(
    (e) => {
      let client = parseInt(e.target.value)
      setGenerateClicked(false)
      if (client === -1) {
        client = undefined
      }
      setSelectedClient(client)

      if (client) {
        dispatch(getHarvestsByClient(client, selectedYear))
      } else {
        dispatch(getHarvestsByYear(selectedYear))
      }
    },
    [selectedYear],
  )

  const onFarmChange = useCallback(
    (e) => {
      let farm = parseInt(e.target.value)
      setGenerateClicked(false)
      if (farm === -1) {
        farm = undefined
      }

      setSelectedFarm(farm)
      if (farm) {
        dispatch(getHarvestsByFarm(farm, selectedYear))
      } else if (selectedClient) {
        dispatch(getHarvestsByClient(selectedClient, selectedYear))
      } else {
        dispatch(getHarvestsByYear(selectedYear))
      }
    },
    [selectedYear],
  )

  useEffect(() => {
    if (allFields && harvestsWithYears && harvestsWithYears.length && crops) {
      let final = []
      harvestsWithYears.map((item) => {
        if (
          allFields?.find((f) => f.id === item.fieldId) != undefined &&
          crops?.find((c) => c.id === item.cropId) != undefined
        ) {
          let { fieldId, cropId } = item
          item.fieldName = allFields?.find((f) => f.id === fieldId).name
          item.cropName = crops?.find((c) => c.id === cropId).name
          if (
            item.status === 'LAYER_PROCESSING_COMPLETE' &&
            item.stats !== null
          ) {
            final = [...final, item]
          }
        }
        const sortedHarvests = final.sort((a, b) => {
          return a.fieldName.localeCompare(b.fieldName, undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        })

        setHarvestsWithMaps(sortedHarvests)
      })
    } else {
      setHarvestsWithMaps([])
    }
  }, [harvestsWithYears, allFields, crops])

  useEffect(() => {
    if (harvestsWithMaps && harvestsWithMaps?.length) {
      setSelectedHarvest(harvestsWithMaps[0])
    } else {
      setSelectedHarvest([])
    }
    if (generateClicked) {
      setHarvestToReport(harvestsWithMaps?.length ? harvestsWithMaps[0] : [])
    }
  }, [harvestsWithMaps])

  const onHarvestChange = useCallback((val) => {
    setLayerToReport({})
    setSelectedHarvest(JSON.parse(val.target.value))
  })

  const layerTypeChange = useCallback((val) => {
    setSelectedLayerType(JSON.parse(val.target.value))
    setSwathSldExtension(undefined)
  })

  const sendSelectedHarvest = useCallback(() => {
    setHarvestToReport(selectedHarvest)
    setLayerToReport(selectedLayer)
    setSwathSldExtensionToReport(swathSldExtension)
    let type = selectedLayer.key
    if (selectedLayer.key === 'swath' && swathSldExtension) {
      type += `-${swathSldExtension}`
    }
    const data = {
      year: selectedYear,
      type: type,
    }
    trackEvent('generate-report', data)
  }, [selectedLayer, selectedHarvest, swathSldExtension, selectedYear])

  const handleGenerate = useCallback(() => {
    let data = {}
    if (reportType === 'farm') {
      data = {
        client: selectedClient,
        farm: selectedFarm,
        year: selectedYear,
        format: format,
        includeDryYield: includeDryYield,
        type: 'farm-report',
      }
      dispatch(
        startFarmReport({
          farmId: selectedFarm,
          year: parseInt(selectedYear),
          format: format,
          includeDryYield: includeDryYield,
        }),
      )
    } else if (reportType === 'field') {
      data = {
        year: selectedYear,
        format: 'csv',
        startDate: startDate || undefined,
        clientId: selectedClient || undefined,
        farmId: selectedFarm || undefined,
        areaUnit: areaUnit || undefined,
        yieldUnit: yieldUnit || undefined,
        totalYieldUnit: totalYieldUnit || undefined,
        type: 'field-report',
      }
      dispatch(
        startFieldReport({
          year: parseInt(selectedYear),
          format: 'csv',
          startDate: startDate || undefined,
          clientId: selectedClient || undefined,
          farmId: selectedFarm || undefined,
          areaUnit: areaUnit || undefined,
          yieldUnit: yieldUnit || undefined,
          totalYieldUnit: totalYieldUnit || undefined,
        }),
      )
    } else if (reportType === 'harvester') {
      data = {
        year: selectedYear,
        format: 'csv',
        startDate: startDate || undefined,
        clientId: selectedClient || undefined,
        farmId: selectedFarm || undefined,
        areaUnit: areaUnit || undefined,
        yieldUnit: yieldUnit || undefined,
        totalYieldUnit: totalYieldUnit || undefined,
        speedUnit: userPrefs?.units?.speed_in || undefined,
        type: 'harvester-report',
      }
      dispatch(
        startHarvesterReport({
          year: parseInt(selectedYear),
          format: 'csv',
          startDate: startDate || undefined,
          clientId: selectedClient || undefined,
          farmId: selectedFarm || undefined,
          areaUnit: areaUnit || undefined,
          yieldUnit: yieldUnit || undefined,
          totalYieldUnit: totalYieldUnit || undefined,
          speedUnit: speedUnit || undefined,
        }),
      )
    } else {
      sendSelectedHarvest()
      return
    }
    trackEvent('generate-report', data)
  }, [
    reportType,
    selectedClient,
    selectedFarm,
    selectedYear,
    format,
    includeDryYield,
    startDate,
    areaUnit,
    yieldUnit,
    totalYieldUnit,
    speedUnit,
    sendSelectedHarvest,
  ])

  return (
    <Box
      sx={{
        width: 1,
        display: 'flex',
      }}
    >
      <CommonSidebar>
        <Box
          sx={{
            mx: 1,
          }}
        >
          <Box>
            <FormControl component="fieldset" fullWidth>
              <FormGroup aria-label="position">
                <Box
                  sx={{
                    my: 1,
                  }}
                >
                  <Box
                    sx={{
                      mx: 1,
                    }}
                  >
                    <FormControlLabel
                      sx={{ display: 'flex', alignItems: 'flex-start' }}
                      control={
                        <TextField
                          id="outlined-select-report-type"
                          select
                          name={t('reports')}
                          slotProps={{ select: { native: true } }}
                          variant="outlined"
                          size="small"
                          value={reportType}
                          onChange={(e) => setReportType(e.target.value)}
                        >
                          <option value="field_harvest">
                            {t('field_harvest')}
                          </option>
                          <option value="farm">{t('farm')}</option>
                          <option value="field">{t('field_report')}</option>
                          <option value="harvester">
                            {t('harvester_report')}
                          </option>
                        </TextField>
                      }
                      label={t('reports')}
                      labelPlacement="top"
                    />
                  </Box>
                  <Box
                    sx={{
                      mx: 1,
                      my: 2,
                    }}
                  >
                    <FormControlLabel
                      sx={{ display: 'flex', alignItems: 'flex-start' }}
                      control={
                        <TextField
                          id="outlined-select-year-native"
                          select
                          onChange={onYearChange}
                          name={t('year')}
                          slotProps={{ select: { native: true } }}
                          variant="outlined"
                          size="small"
                          value={selectedYear}
                        >
                          {harvestYears && Object.keys(harvestYears) ? (
                            harvestYears.map((item) => (
                              <option
                                value={item.harvestYr}
                                key={item.harvestYr}
                              >
                                {item.harvestYr}
                              </option>
                            ))
                          ) : (
                            <></>
                          )}
                        </TextField>
                      }
                      label={t('year')}
                      labelPlacement="top"
                    />
                  </Box>
                  {cffPreferences?.enabled ||
                  reportType === 'farm' ||
                  reportType === 'field' ||
                  reportType === 'harvester' ? (
                    <>
                      <Box
                        sx={{
                          mx: 1,
                          my: 2,
                        }}
                      >
                        <FormControlLabel
                          sx={{ display: 'flex', alignItems: 'flex-start' }}
                          control={
                            <TextField
                              id="outlined-select-client-native"
                              select
                              name={t('client')}
                              slotProps={{ select: { native: true } }}
                              variant="outlined"
                              size="small"
                              value={selectedClient}
                              onChange={onClientChange}
                            >
                              {clients.map((client) => {
                                return (
                                  <option value={client.id} key={client.id}>
                                    {client.name}
                                  </option>
                                )
                              })}
                            </TextField>
                          }
                          label={t('client')}
                          labelPlacement="top"
                        />
                      </Box>
                      <Box
                        sx={{
                          mx: 1,
                          my: 2,
                        }}
                      >
                        <FormControlLabel
                          sx={{ display: 'flex', alignItems: 'flex-start' }}
                          control={
                            <TextField
                              id="outlined-select-farm-native"
                              select
                              name={t('farm')}
                              slotProps={{ select: { native: true } }}
                              variant="outlined"
                              size="small"
                              value={selectedFarm}
                              onChange={onFarmChange}
                            >
                              {farms.map((farm) => {
                                return (
                                  <option value={farm.id} key={farm.id}>
                                    {farm.name}
                                  </option>
                                )
                              })}
                            </TextField>
                          }
                          label={t('farm')}
                          labelPlacement="top"
                        />
                      </Box>
                    </>
                  ) : (
                    <></>
                  )}

                  {reportType === 'field_harvest' && (
                    <Box
                      sx={{
                        mx: 1,
                        my: 2,
                      }}
                    >
                      <FormControlLabel
                        sx={{ display: 'flex', alignItems: 'flex-start' }}
                        control={
                          <TextField
                            id="outlined-select-harvest-native"
                            select
                            onChange={onHarvestChange}
                            name={t('harvest')}
                            slotProps={{ select: { native: true } }}
                            variant="outlined"
                            size="small"
                          >
                            {harvestsWithMaps && harvestsWithMaps.length ? (
                              harvestsWithMaps.map((item) => (
                                <option
                                  value={JSON.stringify(item)}
                                  key={item.id}
                                >{`${item['fieldName']} - ${getTranslatedName(
                                  item['cropName'],
                                )}`}</option>
                              ))
                            ) : (
                              <option>{t('no_harvests')}</option>
                            )}
                          </TextField>
                        }
                        label={t('harvest')}
                        labelPlacement="top"
                      />
                    </Box>
                  )}

                  {reportType === 'field_harvest' && (
                    <Box
                      sx={{
                        mx: 1,
                        my: 2,
                      }}
                    >
                      <Stack direction="column" spacing={1}>
                        <FormControlLabel
                          sx={{ display: 'flex', alignItems: 'flex-start' }}
                          control={
                            <TextField
                              select
                              name={`${t('layer_type')}`}
                              slotProps={{ select: { native: true } }}
                              variant="outlined"
                              size="small"
                              onChange={layerTypeChange}
                            >
                              {layerOptions.map((item) => (
                                <option
                                  key={item.key}
                                  value={JSON.stringify(item)}
                                >
                                  {item.name}
                                </option>
                              ))}
                            </TextField>
                          }
                          label={`${t('layer_type')}`}
                          labelPlacement="top"
                        />
                        {selectedLayer.key === 'swath' && (
                          <SwathParameterSelector
                            layerInfoMap={layerInfoMap}
                            swathParameter={swathParameter}
                            onChange={handleSwathParameterChange}
                          />
                        )}
                      </Stack>
                    </Box>
                  )}

                  {reportType === 'farm' && (
                    <Box
                      sx={{
                        mx: 1,
                        my: 2,
                      }}
                    >
                      <FormControlLabel
                        sx={{ display: 'flex', alignItems: 'flex-start' }}
                        control={
                          <TextField
                            select
                            name={t('report_format')}
                            slotProps={{ select: { native: true } }}
                            variant="outlined"
                            size="small"
                            value={format}
                            onChange={(e) => setFormat(e.target.value)}
                          >
                            <option value="xlsx">xlsx</option>
                            <option value="pdf">pdf</option>
                          </TextField>
                        }
                        label={t('report_format')}
                        labelPlacement="top"
                      />
                    </Box>
                  )}

                  {reportType === 'farm' && (
                    <Box
                      sx={{
                        mx: 1,
                        my: 2,
                      }}
                    >
                      <FormControlLabel
                        sx={{ display: 'flex', alignItems: 'flex-start' }}
                        control={
                          <TextField
                            select
                            name={t('include_dry_yield')}
                            slotProps={{ select: { native: true } }}
                            variant="outlined"
                            size="small"
                            value={includeDryYield}
                            onChange={(e) =>
                              setIncludeDryYield(e.target.value === 'true')
                            }
                          >
                            <option value="true">{t('yes')}</option>
                            <option value="false">{t('no')}</option>
                          </TextField>
                        }
                        label={t('include_dry_yield')}
                        labelPlacement="top"
                      />
                    </Box>
                  )}

                  {(reportType === 'field' || reportType === 'harvester') && (
                    <>
                      <Box
                        sx={{
                          mx: 1,
                          my: 2,
                        }}
                      >
                        <FormControlLabel
                          sx={{ display: 'flex', alignItems: 'flex-start' }}
                          control={
                            <TextField
                              type="date"
                              name={t('start_date')}
                              variant="outlined"
                              size="small"
                              value={startDate || ''}
                              onChange={(e) =>
                                setStartDate(e.target.value || undefined)
                              }
                              data-testid={TEST_IDS.fieldReportStartDateInput}
                              slotProps={{
                                inputLabel: {
                                  shrink: true,
                                },
                              }}
                            />
                          }
                          label={t('start_date')}
                          labelPlacement="top"
                        />
                      </Box>
                    </>
                  )}
                </Box>
              </FormGroup>
            </FormControl>
          </Box>
          <Box
            sx={{
              mx: 1,
              zIndex: 99,
            }}
          >
            <div>
              <Button
                variant="contained"
                onClick={handleGenerate}
                sx={{ width: '160px' }}
                endIcon={
                  <ArrowForwardIcon
                    sx={{
                      '& path': {
                        fill: colors.white,
                      },
                    }}
                  />
                }
                disabled={
                  reportType === 'field_harvest'
                    ? !(
                        selectedHarvest &&
                        Object.keys(selectedHarvest).length &&
                        activeStatus
                      )
                    : reportType === 'field' || reportType === 'harvester'
                      ? !activeStatus
                      : !(selectedFarm && activeStatus)
                }
              >
                {t('generate')}
              </Button>
              {!activeStatus && (
                <>
                  <Box
                    sx={{
                      borderLeft: `8px solid ${alpha(colors.dangerAccent, 0.42)} !important`,
                      background: `${alpha(colors.dangerAccent, 0.1)} !important`,
                      color: 'red !important',
                      marginTop: '16px',
                    }}
                  >
                    <SubscriptionsIcon
                      size="small"
                      style={{
                        color: colors.dangerAccent,
                        height: '0.7em',
                        verticalAlign: 'sub',
                      }}
                      titleAccess={t('no_subscriptions_raw_points')}
                    />
                    <span> {t('renew_subscription')}</span>
                  </Box>
                  <Box sx={{ mt: 2 }}>{t('premium_required_report')}</Box>
                </>
              )}
            </div>
          </Box>
        </Box>
        <Outlet />
      </CommonSidebar>
      <HarvestReport
        harvestObj={harvestToReport}
        layerName={layerToReport}
        swathSldExtension={swathSldExtensionToReport}
        swathParameter={swathParameter}
      />
    </Box>
  )
}
